import { useMemo, useState } from 'react';
import { useOrganizations } from '@/domains/accounts/services/accounts';
import { useAddProductToOrganization } from '@/domains/products/services/products';
import { Product } from '@/shared/types/productTypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Loader2, Search, User } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import EmptyView from '@/shared/components/EmptyView';
import SearchBar from '@/shared/components/SearchBar';

interface AddProductToAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  product: Product;
  selectedRegionCode: string;
}

export default function AddProductToAccountsDialog({
  open,
  onOpenChange,
  onSuccess,
  product,
  selectedRegionCode,
}: AddProductToAccountsDialogProps) {
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizations(open, selectedRegionCode);
  const addProductToOrganization = useAddProductToOrganization(product.orbiusProductReferenceId);

  // Filter out accounts that are already associated with the product
  const availableOrganizations = useMemo(() => {
    return organizations?.filter(organization =>
      !product.organizations?.some(associatedOrganization =>
        associatedOrganization.organizationId === organization.organizationId
      )
    ) || [];
  }, [organizations, product.organizations]);

  // Filter organizations based on search term
  const filteredOrganizations = useMemo(() => {
    return availableOrganizations.filter((organization) =>
      organization.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      organization.organizationId.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
  }, [availableOrganizations, searchTerm]);

  const handleSubmit = async () => {
    if (selectedOrganizationIds.length === 0) {
      showError(ERROR.SUBSCRIPTION.SELECT_ACCOUNT);
      return;
    }

    try {
      await Promise.all(
        selectedOrganizationIds.map((organizationId) =>
          addProductToOrganization.mutateAsync(organizationId)
        )
      );

      showSuccess(SUCCESS.SUBSCRIPTION.ADDED_TO_ACCOUNT);
      onSuccess();
      onOpenChange(false);
      setSelectedOrganizationIds([]);
      setSearchTerm("");
    } catch (error) {
      showErrorWithApiError(error, ERROR.SUBSCRIPTION.ADD_TO_ACCOUNT);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setSearchTerm("");
      setSelectedOrganizationIds([]);
    }
    onOpenChange(open);
  };

  const handleOrganizationToggle = (organizationId: string) => {
    setSelectedOrganizationIds((prev) =>
      prev.includes(organizationId)
        ? prev.filter((id) => id !== organizationId)
        : [...prev, organizationId]
    );
  };

  const showSearch = availableOrganizations.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Subscription to Organizations</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4">
            <h4 className="font-medium mb-2">Subscription Details</h4>
            <p className="text-sm">
              {product.orbiusProductName}
            </p>
          </div>

          <div className="space-y-4">
            {/* Search Input - only show when there are accounts */}
            {showSearch && (
              <div className="relative">
                <SearchBar value={searchTerm} onChange={(value) => setSearchTerm(value)} placeholder="Search accounts..." />
              </div>
            )}

            <h4 className="font-medium">Select Accounts</h4>
            {isLoadingOrganizations ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : filteredOrganizations.length === 0 ? (
              searchTerm ? (
                <EmptyView
                  icon={Search}
                  size="small"
                  title="No Accounts Found"
                  description={`No accounts match "${searchTerm}". Try adjusting your search.`}
                />
              ) : (
                <EmptyView
                  icon={User}
                  size="small"
                  title="No Accounts Available"
                  description="No available accounts to add this subscription to. All accounts already have this subscription."
                />
              )
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {filteredOrganizations.map((organization) => (
                  <div key={organization.organizationId} className="flex items-center space-x-2">
                    <Checkbox
                      id={organization.organizationId}
                      checked={selectedOrganizationIds.includes(organization.organizationId)}
                      onCheckedChange={() => handleOrganizationToggle(organization.organizationId)}
                    />
                    <Label
                      htmlFor={organization.organizationId}
                      className="text-sm font-normal cursor-pointer flex-1"
                    >
                      <div className="flex flex-col">
                        <span>{organization.name}</span>
                        <span className="text-xs">ID: {organization.organizationId}</span>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => handleDialogClose(false)}
            disabled={addProductToOrganization.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={addProductToOrganization.isPending || availableOrganizations.length === 0}
            className="gap-2">
            {addProductToOrganization.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              'Add to Selected Accounts'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 